import { useCallback, useEffect, useState } from "react";
import { schoolsApi, LEVELS } from "@/lib/schoolsApi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import FileUpload from "@/components/FileUpload";
import { Plus, PencilSimple, Trash, Star, X, GraduationCap, Prohibit, Play, UserCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { phoneError } from "@/lib/phone";

const backend = process.env.REACT_APP_BACKEND_URL;
const resolveMedia = (u) => (!u ? "" : /^https?:/.test(u) ? u : `${backend}/api/files/${u}`);

const EMPTY = {
  name: "", level: "shs", levels: [], description: "",
  photos: [], intro_video_hls: "", logo_url: "",
  fees_min: "", fees_max: "", fees_currency: "USD", fees_notes: "",
  country: "", region: "", city: "", address_line: "",
  career_tags: [], programs: [], grades_required: "",
  admission_criteria: "", admission_open: true,
  contact_email: "", contact_phone: "", website: "",
  featured: false,
};

export default function SchoolsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);       // school object or "new"
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await schoolsApi.adminList();
      setItems(r.items || []);
    } catch { toast.error("Failed to load schools"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (sid) => {
    if (!window.confirm("Remove this school? (soft delete)")) return;
    try {
      await schoolsApi.adminDelete(sid);
      toast.success("School removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const action = async (sid, act) => {
    try {
      await schoolsApi.adminAction(sid, act);
      toast.success(`Marked ${act}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="schools-admin">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-black flex items-center gap-2">
            <GraduationCap size={26} weight="fill" className="text-indigo-400" /> Schools directory
          </h2>
          <p className="text-white/60 text-sm">Curate every school from Creche to University.</p>
        </div>
        <Button data-testid="school-new-btn" onClick={() => setEditing({ ...EMPTY, __new: true })} className="bg-indigo-600 hover:bg-indigo-700 gap-2">
          <Plus size={16} weight="bold" /> New school
        </Button>
      </div>

      {loading ? (
        <p className="text-white/50 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-white/50 text-sm">No schools yet. Click <b>New school</b> to add the first one.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((s) => (
            <Card key={s.school_id} className="bg-[#121212] border-white/10" data-testid={`school-admin-row-${s.school_id}`}>
              <CardContent className="p-4 flex gap-3">
                <div className="w-16 h-16 rounded-lg bg-black overflow-hidden shrink-0">
                  {(s.photos?.[0] || s.logo_url) ? (
                    <img src={resolveMedia(s.photos?.[0] || s.logo_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20">
                      <GraduationCap size={28} weight="light" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-heading font-black text-sm truncate">{s.name}</p>
                    {s.status === "removed" && <Badge className="bg-red-500/20 text-red-300 border-red-500/40">Removed</Badge>}
                    {s.featured && <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/40">Featured</Badge>}
                    {s.admission_open ? (
                      <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">Open</Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-300 border-red-500/40">Closed</Badge>
                    )}
                  </div>
                  <p className="text-xs text-white/50 truncate">
                    {[
                      // Show all levels if the school has multi-level; else fall back to the single legacy level.
                      (s.levels && s.levels.length)
                        ? s.levels.map((lv) => LEVELS.find((l) => l.value === lv)?.label).filter(Boolean).join(" · ")
                        : LEVELS.find((l) => l.value === s.level)?.label,
                      s.city, s.country,
                    ].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-xs text-white/60 line-clamp-2 mt-1">{s.description}</p>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    <Button data-testid={`school-edit-${s.school_id}`} size="sm" variant="outline" className="border-white/20 bg-transparent hover:bg-white/5" onClick={() => setEditing(s)}>
                      <PencilSimple size={12} weight="bold" className="mr-1" /> Edit
                    </Button>
                    <Button
                      data-testid={`school-feature-${s.school_id}`}
                      size="sm"
                      variant="outline"
                      className="border-yellow-500/40 bg-transparent hover:bg-yellow-500/10 text-yellow-300"
                      onClick={() => action(s.school_id, s.featured ? "unfeature" : "feature")}
                    >
                      <Star size={12} weight="fill" className="mr-1" /> {s.featured ? "Unfeature" : "Feature"}
                    </Button>
                    <Button
                      data-testid={`school-toggle-adm-${s.school_id}`}
                      size="sm"
                      variant="outline"
                      className="border-white/20 bg-transparent hover:bg-white/5"
                      onClick={() => action(s.school_id, s.admission_open ? "pause" : "resume")}
                    >
                      {s.admission_open ? <Prohibit size={12} weight="bold" className="mr-1" /> : <Play size={12} weight="fill" className="mr-1" />}
                      {s.admission_open ? "Close" : "Open"} admissions
                    </Button>
                    {s.status === "removed" ? (
                      <Button size="sm" variant="outline" className="border-emerald-500/40 bg-transparent hover:bg-emerald-500/10 text-emerald-300" onClick={() => action(s.school_id, "restore")}>
                        Restore
                      </Button>
                    ) : (
                      <Button data-testid={`school-delete-${s.school_id}`} size="sm" variant="outline" className="border-red-500/40 bg-transparent hover:bg-red-500/10 text-red-400" onClick={() => remove(s.school_id)}>
                        <Trash size={12} weight="bold" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <SchoolEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/* ==================== EDITOR MODAL ==================== */
function SchoolEditor({ initial, onClose, onSaved }) {
  const [s, setS] = useState({
    ...EMPTY,
    ...initial,
    // ensure array-ish
    photos: initial?.photos || [],
    // Multi-level: seed from the doc's `levels[]` when present, else from the
    // legacy single `level` so the picker keeps the school's current level pre-selected.
    levels: (initial?.levels && initial.levels.length)
      ? initial.levels
      : (initial?.level ? [initial.level] : []),
    career_tags: initial?.career_tags || [],
    programs: initial?.programs || [],
    fees_min: initial?.fees_min ?? "",
    fees_max: initial?.fees_max ?? "",
  });
  const [saving, setSaving] = useState(false);
  const isNew = !!s.__new;
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const save = async () => {
    if (!s.name?.trim()) { toast.error("Name is required"); return; }
    if (s.contact_phone && phoneError(s.contact_phone)) { toast.error(phoneError(s.contact_phone)); return; }
    const payload = {
      ...s,
      fees_min: s.fees_min === "" ? null : Number(s.fees_min),
      fees_max: s.fees_max === "" ? null : Number(s.fees_max),
    };
    delete payload.__new;
    delete payload.school_id;
    delete payload.status;
    delete payload.created_by;
    delete payload.created_at;
    delete payload.updated_at;
    setSaving(true);
    try {
      if (isNew) await schoolsApi.adminCreate(payload);
      else await schoolsApi.adminUpdate(s.school_id, payload);
      toast.success(isNew ? "School created" : "School updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{isNew ? "Add school" : `Edit — ${s.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Section title="Basics">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="School name*">
                <Input data-testid="editor-name" value={s.name || ""} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border-white/10" />
              </Field>
              <Field label="Levels (select all that apply)" full>
                <div className="flex flex-wrap gap-2" data-testid="editor-levels">
                  {LEVELS.map((l) => {
                    const active = (s.levels || []).includes(l.value);
                    return (
                      <button
                        type="button"
                        key={l.value}
                        data-testid={`level-chip-${l.value}`}
                        onClick={() =>
                          setS((x) => {
                            const nextLevels = active
                              ? x.levels.filter((v) => v !== l.value)
                              : [...(x.levels || []), l.value];
                            // Keep the legacy single `level` in sync with the first selected chip
                            const nextLevel = nextLevels[0] || x.level;
                            return { ...x, levels: nextLevels, level: nextLevel };
                          })
                        }
                        className={
                          "px-3 py-1.5 rounded-full text-xs border transition-colors " +
                          (active
                            ? "bg-indigo-500/20 border-indigo-400 text-indigo-100"
                            : "bg-white/5 border-white/10 text-white/70 hover:border-white/30")
                        }
                      >
                        {l.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-white/40 mt-2">
                  A school can serve multiple levels (e.g. Primary + JHS + SHS). The first pick becomes the
                  default when displayed as a single level.
                </p>
              </Field>
              <Field label="Description" full>
                <Textarea data-testid="editor-desc" value={s.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} className="bg-white/5 border-white/10" />
              </Field>
            </div>
          </Section>

          <Section title="Media">
            <MultiPhotoUploader
              value={s.photos || []}
              onChange={(arr) => set("photos", arr)}
            />
            <Field label="Intro video HLS URL (.m3u8) — optional">
              <Input data-testid="editor-video" value={s.intro_video_hls || ""} onChange={(e) => set("intro_video_hls", e.target.value)} placeholder="https://.../master.m3u8" className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Logo URL — optional">
              <Input value={s.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="uploads/… or full URL" className="bg-white/5 border-white/10" />
            </Field>
          </Section>

          <Section title="Fees">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field label="Min fee / year">
                <Input data-testid="editor-fees-min" type="number" value={s.fees_min ?? ""} onChange={(e) => set("fees_min", e.target.value)} className="bg-white/5 border-white/10" />
              </Field>
              <Field label="Max fee / year">
                <Input data-testid="editor-fees-max" type="number" value={s.fees_max ?? ""} onChange={(e) => set("fees_max", e.target.value)} className="bg-white/5 border-white/10" />
              </Field>
              <Field label="Currency">
                <Input value={s.fees_currency || "USD"} onChange={(e) => set("fees_currency", e.target.value)} className="bg-white/5 border-white/10" />
              </Field>
              <Field label="Fees notes" full>
                <Input value={s.fees_notes || ""} onChange={(e) => set("fees_notes", e.target.value)} placeholder="Boarding vs day, extras…" className="bg-white/5 border-white/10" />
              </Field>
            </div>
          </Section>

          <Section title="Location">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Country">
                <Input data-testid="editor-country" value={s.country || ""} onChange={(e) => set("country", e.target.value)} className="bg-white/5 border-white/10" />
              </Field>
              <Field label="Region / State">
                <Input value={s.region || ""} onChange={(e) => set("region", e.target.value)} className="bg-white/5 border-white/10" />
              </Field>
              <Field label="City">
                <Input data-testid="editor-city" value={s.city || ""} onChange={(e) => set("city", e.target.value)} className="bg-white/5 border-white/10" />
              </Field>
              <Field label="Address">
                <Input value={s.address_line || ""} onChange={(e) => set("address_line", e.target.value)} className="bg-white/5 border-white/10" />
              </Field>
            </div>
          </Section>

          <Section title="Career & academics">
            <Field label="Career tags (comma separated)">
              <Input
                data-testid="editor-careers"
                value={(s.career_tags || []).join(", ")}
                onChange={(e) => set("career_tags", e.target.value.split(",").map((v) => v.trim()).filter(Boolean))}
                placeholder="Medicine, Engineering, Business…"
                className="bg-white/5 border-white/10"
              />
            </Field>
            <Field label="Programs (comma separated)">
              <Input
                data-testid="editor-programs"
                value={(s.programs || []).join(", ")}
                onChange={(e) => set("programs", e.target.value.split(",").map((v) => v.trim()).filter(Boolean))}
                placeholder="Science, Business, Arts…"
                className="bg-white/5 border-white/10"
              />
            </Field>
            <Field label="Grades required">
              <Input data-testid="editor-grades" value={s.grades_required || ""} onChange={(e) => set("grades_required", e.target.value)} placeholder="e.g. 'Passes A-B in core subjects'" className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Admission criteria">
              <Textarea data-testid="editor-adm-criteria" value={s.admission_criteria || ""} onChange={(e) => set("admission_criteria", e.target.value)} rows={3} className="bg-white/5 border-white/10" />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="editor-adm-open" type="checkbox" checked={!!s.admission_open} onChange={(e) => set("admission_open", e.target.checked)} />
              Admissions currently open
            </label>
          </Section>

          <Section title="Contact">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Email">
                <Input data-testid="editor-email" type="email" value={s.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="bg-white/5 border-white/10" />
              </Field>
              <Field label="Phone (with + country code)">
                <Input
                  data-testid="editor-phone"
                  value={s.contact_phone || ""}
                  onChange={(e) => {
                    let v = e.target.value;
                    if (v && !v.startsWith("+")) v = "+" + v.replace(/^\++/, "");
                    set("contact_phone", v);
                  }}
                  placeholder="+233 XX XXX XXXX"
                  className="bg-white/5 border-white/10"
                />
                {s.contact_phone && phoneError(s.contact_phone) && (
                  <p className="text-[11px] text-red-400 mt-1">{phoneError(s.contact_phone)}</p>
                )}
              </Field>
              <Field label="Website" full>
                <Input value={s.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://…" className="bg-white/5 border-white/10" />
              </Field>
            </div>
          </Section>

          {/* Headteacher assignment — only shown for existing schools since
              we need a real school_id to POST against. The email set here
              becomes the account that can log into the Headteacher portal. */}
          {!isNew && (
            <HeadteacherAssignSection
              schoolId={s.school_id}
              currentEmail={s.headteacher_email}
              currentName={s.headteacher_name}
              onAssigned={(updated) => setS((x) => ({
                ...x,
                headteacher_email: updated.headteacher_email,
                headteacher_name: updated.headteacher_name,
              }))}
            />
          )}

          <label className="flex items-center gap-2 text-sm">
            <input data-testid="editor-featured" type="checkbox" checked={!!s.featured} onChange={(e) => set("featured", e.target.checked)} />
            Mark as featured
          </label>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" className="border-white/20 bg-transparent hover:bg-white/5" onClick={onClose}>Cancel</Button>
          <Button data-testid="editor-save" onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
            {saving ? "Saving…" : (isNew ? "Create school" : "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== HELPERS ==================== */
function HeadteacherAssignSection({ schoolId, currentEmail, currentName, onAssigned }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const assign = async () => {
    const clean = (email || "").trim().toLowerCase();
    if (!clean || !clean.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const r = await schoolsApi.assignHeadteacher(schoolId, clean, name.trim());
      toast.success(`Headteacher set to ${clean}`);
      onAssigned?.(r.school || { headteacher_email: clean, headteacher_name: name.trim() });
      setEmail("");
      setName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign headteacher");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/[0.04] p-4 space-y-3">
      <h3 className="font-heading font-bold text-fuchsia-200 text-sm uppercase tracking-widest flex items-center gap-2">
        <UserCircle size={16} weight="fill" /> Headteacher
      </h3>

      {currentEmail ? (
        <div
          className="flex items-center gap-2 rounded-md bg-white/[0.04] border border-white/10 px-3 py-2 text-sm"
          data-testid="ht-current"
        >
          <UserCircle size={18} weight="fill" className="text-fuchsia-300" />
          <div className="min-w-0 flex-1">
            <p className="text-white truncate">{currentName || "Assigned"}</p>
            <p className="text-white/50 text-xs truncate">{currentEmail}</p>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Active</span>
        </div>
      ) : (
        <p className="text-xs text-amber-300/80">
          No headteacher assigned yet. The person you add here will be able to log into the Headteacher portal
          for this school using this email.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Headteacher email">
          <Input
            data-testid="ht-assign-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="head.teacher@example.com"
            className="bg-white/5 border-white/10"
          />
        </Field>
        <Field label="Full name (optional)">
          <Input
            data-testid="ht-assign-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            className="bg-white/5 border-white/10"
          />
        </Field>
      </div>

      <Button
        data-testid="ht-assign-btn"
        onClick={assign}
        disabled={busy}
        className="bg-fuchsia-500 hover:bg-fuchsia-600 text-black font-bold gap-2"
      >
        <PaperPlaneTilt size={14} weight="fill" />
        {busy ? "Assigning…" : currentEmail ? "Replace headteacher" : "Assign headteacher"}
      </Button>
      <p className="text-[11px] text-white/40">
        The user must sign in with this same email address to unlock the Headteacher portal.
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3">
      <h3 className="font-heading font-bold text-white/80 text-sm uppercase tracking-widest">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function MultiPhotoUploader({ value, onChange }) {
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Photos ({value.length})</label>
      {value.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {value.map((p, i) => (
            <div key={i} className="relative w-24 h-24 rounded overflow-hidden shrink-0 bg-black">
              <img src={resolveMedia(p)} alt="" className="w-full h-full object-cover" />
              <button
                data-testid={`school-photo-remove-${i}`}
                onClick={() => remove(i)}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center"
              >
                <X size={10} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
      <FileUpload
        accept="image/*"
        label="Add photo"
        testId="school-photo-upload"
        value=""
        onChange={(path) => { if (path) onChange([...value, path]); }}
      />
    </div>
  );
}
